import { useCallback, useEffect, useRef, useState } from 'react';

import { IGenericTableEntity } from '/common/models';

import { getInitialQuery } from '../../shared/Table/helpers/getInitialQuery';
import { ITableQuery } from '../../shared/Table/interfaces/ITableQuery';
import { ITableColumnSort } from '../../shared/Table/interfaces/ITableColumnSort';
import { ITableColumnsFilter } from '../../shared/Table/interfaces/ITableColumnsFilter';

import {
  TableQueryFilterParameter,
  TableQueryFilterParameterSerialized,
  IUseTableQueryChangeParams,
  ITableQueryConfig,
} from './types';
import { useTableWorker } from './useTableWorker';

const TIME_TO_WAIT_FOR_QUERY_WITHOUT_LOADER = 100;

function getSerializedFilterParameters(
  filterParameters: TableQueryFilterParameter[]
): TableQueryFilterParameterSerialized[] {
  return filterParameters.map(([queryField, filter]) => [
    queryField,
    {
      type: filter.getClassName(),
      filter: filter.serialize(),
    },
  ]);
}

/**
 * A hook that helps to perform sorting and filtering queries
 * on tables where this logic is supposed to be handled on UI.
 * @param tableData - full (static) table data that can be changed only by a server request
 * @param tableEntriesSettingHandler - a handler responsible for setting (dynamic) entries currently presented in a table
 * @param tableQuery (optional) - initial query config to store for own purposes (is returned afterall)
 * @param isTableProcessingSettingHandler - a handler responsible for setting a current table "processing" status
 * @returns a tuple that consists of the latest query and a function for performing the last ones.
 */
export function useTableQueryChange({
  tableData,
  tableEntriesSettingHandler,
  tableQuery = getInitialQuery(),
  isTableProcessingSettingHandler,
}: IUseTableQueryChangeParams): [ITableQuery<string>, (qr: ITableQuery<string>) => void] {
  const [query, setQuery] = useState<ITableQuery<string>>(tableQuery);
  const tableDataRef = useRef<IGenericTableEntity[]>(tableData);
  const worker = useTableWorker();
  const handleQueryChange = useCallback(
    async ({
      tableData: dataForWorker,
      filteringParams,
      sortingParams,
    }: Pick<ITableQueryConfig, 'tableData' | 'filteringParams' | 'sortingParams'>) => {
      if (!worker) return;
      let isQueryWaitingTimeLeft = false;

      const timeoutId = setTimeout(() => {
        isQueryWaitingTimeLeft = true;
        isTableProcessingSettingHandler(true);
      }, TIME_TO_WAIT_FOR_QUERY_WITHOUT_LOADER);

      const handleWorkerMessage = ({ data }: MessageEvent) => {
        tableEntriesSettingHandler(data);
        clearTimeout(timeoutId);
        if (isQueryWaitingTimeLeft) {
          isTableProcessingSettingHandler(false);
        }
        worker.removeEventListener('message', handleWorkerMessage);
      };

      worker.addEventListener('message', handleWorkerMessage);

      worker.postMessage({
        tableData: dataForWorker,
        filteringParams: getSerializedFilterParameters(Object.entries(filteringParams)),
        sortingParams,
      });
    },
    [worker, isTableProcessingSettingHandler, tableEntriesSettingHandler]
  );

  const changeQuery = useCallback(
    (qr: ITableQuery<string>) => {
      const sortingParams: ITableColumnSort<string>[] = qr?.sortBy;
      const filteringParams: ITableColumnsFilter = qr?.filterBy;

      if (typeof qr !== 'object' || typeof filteringParams !== 'object' || typeof sortingParams !== 'object') {
        return;
      }

      setQuery(qr);

      const data: IGenericTableEntity[] = tableDataRef.current.slice();

      if (!Object.entries(filteringParams).length && !sortingParams.length) {
        tableEntriesSettingHandler(data);

        return;
      }

      handleQueryChange({
        tableData: data,
        filteringParams,
        sortingParams,
      });
    },
    [tableDataRef, tableEntriesSettingHandler, handleQueryChange]
  );

  useEffect(() => {
    tableDataRef.current = tableData;

    changeQuery({ ...query });
    // this should be called only when tableData is modified
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData]);

  return [query, changeQuery];
}
